/* eslint-disable react-refresh/only-export-components -- This file exports
   a route MANIFEST, not components. Its screens are defined inside a
   factory so they can close over the API client, which puts them beyond
   Fast Refresh's reach whatever this file exports. Editing a screen
   refreshes normally; editing this file re-mounts the router, which is
   correct — the route table changed. */
/**
 * Routes for the pages the router redirects to.
 *
 * `/forbidden` and `/loading` are not decorative: the access gate and the
 * bootstrap gate redirect to them by path, so an application that omits this
 * feature would redirect into a 404. They are part of the routing contract,
 * not optional extras.
 */

import { useNavigate } from "@tanstack/react-router";
import { BOOTSTRAP_PATH, FORBIDDEN_PATH, type FeatureRoutes } from "@hardauto/core-router";
import { LoadingState } from "@hardauto/core-ui";
import { useTranslation } from "react-i18next";

import {
  ForbiddenScreen,
  NotFoundScreen,
  OfflineScreen,
  ServerErrorScreen,
} from "./screens/ErrorScreens";
import { NAMESPACE } from "./i18n";
import "./i18n";

export const ERROR_PATHS = {
  forbidden: FORBIDDEN_PATH,
  loading: BOOTSTRAP_PATH,
  offline: "/offline",
  serverError: "/error",
} as const;

/**
 * Held here while the session is being restored.
 *
 * Deliberately plain. The gate sends every navigation here for a few hundred
 * milliseconds on a cold load, so anything eye-catching would flash on every
 * visit — and the user did not ask to see a page, they asked to see the one
 * behind it.
 */
function BootstrapScreen() {
  const { t } = useTranslation(NAMESPACE);
  return <LoadingState label={t("loading.restoring")} />;
}

export function errorRoutes(options: { signInHref?: string } = {}): FeatureRoutes {
  function Forbidden() {
    return <ForbiddenScreen {...(options.signInHref ? { signInHref: options.signInHref } : {})} />;
  }

  function Offline() {
    const navigate = useNavigate();
    return (
      <OfflineScreen
        onRetry={() => {
          // A router navigation rather than `location.reload()`: reloading
          // discards the in-memory access token and forces a refresh round
          // trip, so a momentary blip would cost the user a re-authentication.
          void navigate({ to: "/", replace: true });
        }}
      />
    );
  }

  function ServerError() {
    return <ServerErrorScreen />;
  }

  return {
    id: "errors",
    routes: [
      { path: ERROR_PATHS.loading, component: BootstrapScreen, layout: "public" },
      { path: ERROR_PATHS.forbidden, component: Forbidden, layout: "public" },
      { path: ERROR_PATHS.offline, component: Offline, layout: "public" },
      { path: ERROR_PATHS.serverError, component: ServerError, layout: "public" },
    ],
  };
}

export { NotFoundScreen };
